/**
 * Shared marketing-HERO primitives (black-and-white palette, Inter).
 *
 * These are the reusable building blocks every hero section imports — do NOT
 * re-implement them in section files. Styling matches the prototype 1:1 (see
 * the design map §5). Hover / focus rules live in `hero.css` under `.mkhero`.
 */
import { ld } from "@/lib/theme/light-dark";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";

import { BRAND_LOGO_TITLE, MARKETING_LOGO_ALT } from "@/lib/marketing-seo";
import { useTranslations } from "next-intl";
import { H } from "./hero-tokens";

/**
 * Filter / feature pill (prototype: hero feature-chip row + in-app filters).
 * Default = white with a hairline border; `active` = accent fill (ink on the
 * marketing page, verdigris inside the product window).
 */
export function Pill(props: {
  readonly children: ReactNode;
  readonly active?: boolean;
  readonly onClick?: () => void;
  readonly style?: CSSProperties;
}): ReactNode {
  const active = props.active === true;
  return (
    <button
      type="button"
      className="mkhero-chip"
      onClick={props.onClick}
      style={{
        appearance: "none",
        cursor: "pointer",
        fontFamily: H.sans,
        fontSize: "12.5px",
        fontWeight: 500,
        padding: "7px 13px",
        borderRadius: "999px",
        border: `1px solid ${active ? H.accent : ld("#E7E9ED", "#32363a")}`,
        background: active ? H.accent : ld("#fff", "#181b1f"),
        color: active ? ld("#fff", "#181b1f") : H.ink,
        transition: "border-color 120ms ease",
        ...props.style,
      }}
    >
      {props.children}
    </button>
  );
}

/**
 * Primary button: ink by default, `tone="cta"` for the orange Demo-Call buttons.
 * Renders as a `next/link` when `href` is set, otherwise a `<button>`.
 */
export function DarkButton(props: {
  readonly children: ReactNode;
  readonly href?: string;
  readonly onClick?: () => void;
  readonly style?: CSSProperties;
  readonly tone?: "ink" | "cta";
}): ReactNode {
  const cta = props.tone === "cta";
  const className = cta ? "mkhero-ctabtn" : "mkhero-darkbtn";
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "9px",
    background: cta ? H.ctaGradient : H.accent,
    color: cta ? "#fff" : ld("#fff", "#181b1f"),
    padding: "15px 27px",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: 600,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: H.sans,
    ...props.style,
  };
  if (props.href !== undefined) {
    return (
      <Link href={props.href} className={className} style={style}>
        {props.children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      onClick={props.onClick}
      style={style}
    >
      {props.children}
    </button>
  );
}

/**
 * Logo lockup — dark rounded square (#0B0F19) with a white upward triangle SVG
 * plus the bold "hausverwalter.ai" wordmark. `size` is the square's edge in px
 * (sizes 22–36 used across the prototype); the wordmark scales with it.
 * `iconOnly` drops the wordmark (mobile nav, where the long wordmark would crowd
 * out the action links).
 */
export function Logo(props: {
  readonly size?: number;
  readonly wordmark?: string;
  readonly iconOnly?: boolean;
}): ReactNode {
  const t = useTranslations("marketing.heroPrimitives");
  const size = props.size ?? 30;
  const radius = Math.max(6, Math.round(size * 0.26));
  const tri = size * 0.46;
  // Wordmark font-size tracks the square edge (30px square → 18px wordmark).
  const fontSize = Math.round(size * 0.6);
  return (
    <div
      style={{ display: "inline-flex", alignItems: "center", gap: 11 }}
      aria-label={MARKETING_LOGO_ALT}
      title={BRAND_LOGO_TITLE}
    >
      <BrandLogoMark width={size} height={size} variant="dark" />
      {props.iconOnly === true ? null : (
        <span
          style={{
            fontFamily: H.sans,
            fontSize,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: H.ink,
          }}
        >
          {props.wordmark !== undefined
            ? props.wordmark
            : t.rich("wordmark", {
                muted: (chunks) => (
                  <span
                    style={{ color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"), fontWeight: 600 }}
                  >
                    {chunks}
                  </span>
                ),
              })}
        </span>
      )}
    </div>
  );
}

/**
 * Uppercase, letter-spaced eyebrow label (sidebar group headers, stat-card
 * titles, captions). Optional small accent dot/square marker via `dot`.
 */
export function EyebrowLabel(props: {
  readonly children: ReactNode;
  readonly dot?: boolean;
  readonly square?: boolean;
  readonly style?: CSSProperties;
}): ReactNode {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: H.label,
        fontSize: 9.5,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: H.inkGhost,
        ...props.style,
      }}
    >
      {props.dot === true || props.square === true ? (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: props.square === true ? 1 : "50%",
            background: H.accent,
            flexShrink: 0,
          }}
        />
      ) : null}
      {props.children}
    </span>
  );
}

/**
 * Primary accent pill button. Renders as a `next/link` when `href`
 * is set, otherwise a `<button>`.
 */
export function AccentButton(props: {
  readonly children: ReactNode;
  readonly href?: string;
  readonly onClick?: () => void;
  readonly style?: CSSProperties;
}): ReactNode {
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: H.accent,
    color: ld("#fff", "#181b1f"),
    padding: "10px 18px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: H.sans,
    ...props.style,
  };
  if (props.href !== undefined) {
    return (
      <Link href={props.href} className="mkhero-accentbtn" style={style}>
        {props.children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="mkhero-accentbtn"
      onClick={props.onClick}
      style={style}
    >
      {props.children}
    </button>
  );
}

/**
 * Segmented toggle (To-Do / Wiedervorlage / Erledigt, Aus / Vorschlag / Agent,
 * etc.). The active segment is white with a soft shadow on a `#F1F2F4` track.
 */
export function Segmented<T extends string>(props: {
  readonly segments: readonly T[];
  readonly active: T;
  readonly onChange?: (value: T) => void;
  readonly style?: CSSProperties;
  /** `md`: website controls beside body copy; `sm` (default): inside app mocks. */
  readonly size?: "sm" | "md";
  /** Optional icon before a segment's label, keyed by segment. */
  readonly icons?: Readonly<Partial<Record<T, ReactNode>>>;
}): ReactNode {
  const md = props.size === "md";
  return (
    <div
      style={{
        display: "inline-flex",
        background: ld("#F1F2F4", "#2b2e32"),
        borderRadius: md ? 11 : 9,
        padding: 3,
        gap: 2,
        ...props.style,
      }}
    >
      {props.segments.map((seg) => {
        const isActive = seg === props.active;
        const icon = props.icons?.[seg];
        return (
          <button
            key={seg}
            type="button"
            onClick={props.onChange ? () => props.onChange?.(seg) : undefined}
            style={{
              appearance: "none",
              border: "none",
              cursor: props.onChange ? "pointer" : "default",
              padding: md ? "8px 16px" : "5px 12px",
              borderRadius: md ? 8 : 7,
              fontFamily: H.sans,
              fontSize: md ? 15 : 12.5,
              fontWeight: isActive ? 600 : 500,
              background: isActive ? ld("#fff", "#181b1f") : "transparent",
              color: isActive ? H.ink : H.inkFaint,
              boxShadow: isActive ? `0 1px 2px ${ld("rgba(11,15,25,0.08)", "rgba(0,0,0,0.17)")}` : "none",
              transition: md
                ? "background 250ms ease, color 250ms ease, box-shadow 250ms ease"
                : undefined,
              whiteSpace: "nowrap",
              ...(icon !== undefined
                ? {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: md ? "8px 13px" : "5px 10px",
                  }
                : null),
            }}
          >
            {icon}
            {seg}
          </button>
        );
      })}
    </div>
  );
}

// Avatar + StatusDot live in `hero-primitives-app.tsx` (in-app visual atoms).
